import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";
import { Button } from "@/components/uiDir/button";
import { Input } from "@/components/uiDir/input";
import { Label } from "@/components/uiDir/label";
import { Textarea } from "@/components/uiDir/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/uiDir/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/uiDir/select";
import { Checkbox } from "@/components/uiDir/checkbox";import { Switch } from "@/components/uiDir/switch";
import { Loader2, ArrowLeft, X, Upload, Trash2, GripVertical, Users } from "lucide-react";
import { Project, ProjectMedia, ProjectTag, ProjectCollaborator } from "@/lib/models/publicprofileModels";

type ProjectFormData = Omit<Project, 'id' | 'created_at' | 'user_id' | 'views_count' | 'likes_count' | 'display_order' | 'media'> & {
  media: ProjectMedia[];
  tags: string[];
  collaborators: Omit<ProjectCollaborator, 'id' | 'project_id'>[];
};

const EditProject = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectFormData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newTag, setNewTag] = useState("");
  const [showOpportunities, setShowOpportunities] = useState(false);
  const [newOpportunity, setNewOpportunity] = useState("");

  const fetchProject = useCallback(async () => {
    if (!projectId) return;

    try {
      setIsLoading(true);
      const { data: projectData, error: projectError } = await supabase
        .from("projects")
        .select("*")
        .eq("id", projectId)
        .single();

      if (projectError) throw projectError;

      const { data: mediaData } = await supabase
        .from("project_media")
        .select("*")
        .eq("project_id", projectId)
        .order("display_order");

      const { data: tagsData } = await supabase
        .from("project_tags")
        .select("tag")
        .eq("project_id", projectId);

      const { data: collaboratorsData } = await supabase
        .from("project_collaborators")
        .select("collaborator_id, collaborator_name, collaborator_email, role")
        .eq("project_id", projectId)
        .order("display_order");

      setProject({
        ...projectData,
        media: mediaData || [],
        tags: tagsData?.map(t => t.tag) || [],
        collaborators: collaboratorsData || [],
      });
      setShowOpportunities(projectData.opportunities && projectData.opportunities.length > 0);

    } catch (err) {
      console.error("Error fetching project:", err);
      toast.error("Failed to load project data.");
      setError("Could not find the project you're looking for.");
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setProject(prev => prev ? { ...prev, [name]: value } : null);
  };

  const handleCheckboxChange = (name: keyof ProjectFormData, checked: boolean) => {
    setProject(prev => prev ? { ...prev, [name]: checked } : null);
  };

  const handleSelectChange = (name: keyof ProjectFormData, value: string) => {
    setProject(prev => prev ? { ...prev, [name]: value } : null);
  };

  const handleAddTag = () => {
    if (newTag && project && !project.tags.includes(newTag)) {
      setProject({ ...project, tags: [...project.tags, newTag] });
      setNewTag("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    if (project) {
      setProject({ ...project, tags: project.tags.filter(tag => tag !== tagToRemove) });
    }
  };

  const handleAddOpportunity = () => {
    if (newOpportunity.trim() && project) {
      const updatedOpportunities = [...(project.opportunities || []), newOpportunity.trim()];
      setProject({ ...project, opportunities: updatedOpportunities });
      setNewOpportunity("");
    }
  };

  const handleRemoveOpportunity = (opportunityToRemove: string) => {
    if (project) {
      const updatedOpportunities = project.opportunities.filter(
        (op) => op !== opportunityToRemove
      );
      setProject({ ...project, opportunities: updatedOpportunities });
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project || !projectId) return;

    setIsSaving(true);
    toast.info("Saving your changes...");

    try {
      const { media, tags, collaborators, ...projectDetails } = project;

      // If showOpportunities is false, clear the opportunities array before saving
      if (!showOpportunities) {
        projectDetails.opportunities = [];
      }


      // 1. Update project details
      const { error: projectUpdateError } = await supabase
        .from("projects")
        .update(projectDetails)
        .eq("id", projectId);

      if (projectUpdateError) throw projectUpdateError;

      // 2. Sync tags
      // Delete tags that are no longer present
      const { data: existingTags } = await supabase.from('project_tags').select('tag').eq('project_id', projectId);
      const tagsToDelete = existingTags?.filter(et => !tags.includes(et.tag)).map(et => et.tag) || [];
      if (tagsToDelete.length > 0) {
        await supabase.from('project_tags').delete().eq('project_id', projectId).in('tag', tagsToDelete);
      }
      // Add new tags
      const tagsToAdd = tags.filter(t => !existingTags?.some(et => et.tag === t));
      if (tagsToAdd.length > 0) {
        await supabase.from('project_tags').insert(tagsToAdd.map(tag => ({ project_id: projectId, tag })));
      }

      // Media and Collaborators would be handled here as well.
      // This example focuses on the main project details and tags for brevity.

      toast.success("Project updated successfully!");
      navigate(`/dashboard/projects`);

    } catch (err) {
      console.error("Error updating project:", err);
      toast.error("Failed to update project. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="ml-4 text-gray-600">Loading project editor...</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center">
        <div>
          <h2 className="text-2xl font-bold mb-2 text-red-600">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/dashboard/projects">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to My Projects
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 py-8">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 max-w-5xl">
        <Link to="/dashboard">
            <Button variant="ghost" className="mb-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
            </Button>
        </Link>

        <form onSubmit={handleSubmit}>
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-2xl">Edit Project</CardTitle>
              <CardDescription>Update the details for "{project.title}"</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Project Title</Label>
                <Input
                  id="title"
                  name="title"
                  value={project.title}
                  onChange={handleInputChange}
                  placeholder="e.g., My Awesome App"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={project.description}
                  onChange={handleInputChange}
                  placeholder="A short, compelling description of your project."
                  rows={4}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={project.category}
                    onValueChange={(value) => handleSelectChange('category', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Web Development">Web Development</SelectItem>
                      <SelectItem value="Mobile App">Mobile App</SelectItem>
                      <SelectItem value="Graphic Design">Graphic Design</SelectItem>
                      <SelectItem value="3D Model">3D Model</SelectItem>
                      <SelectItem value="Game Development">Game Development</SelectItem>
                      <SelectItem value="Video Production">Video Production</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={project.status}
                    onValueChange={(value) => handleSelectChange('status', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="on_hold">On Hold</SelectItem>
                      <SelectItem value="concept">Concept</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="featured"
                  checked={project.featured}
                  onCheckedChange={(checked) => handleCheckboxChange('featured', checked as boolean)}
                />
                <Label htmlFor="featured" className="cursor-pointer">
                  Feature this project on your profile
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="is_private"
                  checked={project.is_private}
                  onCheckedChange={(checked) => handleCheckboxChange('is_private', checked as boolean)}
                />
                <Label htmlFor="is_private" className="cursor-pointer">
                  Make this project private (only you can see it)
                </Label>
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Project Links</CardTitle>
              <CardDescription>Add relevant links for your project.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="live_url">Live URL</Label>
                <Input id="live_url" name="live_url" value={project.live_url || ''} onChange={handleInputChange} placeholder="https://" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="github_url">GitHub URL</Label>
                <Input id="github_url" name="github_url" value={project.github_url || ''} onChange={handleInputChange} placeholder="https://github.com/..." />
              </div>
              <div className="space-y-2">
                <Label htmlFor="youtube_url">YouTube URL</Label>
                <Input id="youtube_url" name="youtube_url" value={project.youtube_url || ''} onChange={handleInputChange} placeholder="https://youtube.com/watch?v=..." />
              </div>
            </CardContent>
          </Card>

          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Tags</CardTitle>
              <CardDescription>Add tags to improve discoverability.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4">
                {project.tags.map(tag => (
                  <div key={tag} className="flex items-center gap-1 bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)} className="text-blue-500 hover:text-blue-700">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                  placeholder="e.g., React, UI/UX"
                />
                <Button type="button" variant="outline" onClick={handleAddTag}>Add Tag</Button>
              </div>
            </CardContent>
          </Card>

          {/* Opportunities Section */}
          {(project.status === "in_progress" || project.status === "on_hold" || project.status === "concept") && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Collaboration Opportunities</CardTitle>
                <CardDescription>Looking for help? List the roles you need to fill.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="opportunity-switch" className="text-base">
                      Show Opportunities
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Enable to list open roles on your project page.
                    </p>
                  </div>
                  <Switch
                    id="opportunity-switch"
                    checked={showOpportunities}
                    onCheckedChange={setShowOpportunities}
                  />
                </div>

                {showOpportunities && (
                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex gap-2">
                      <Input
                        placeholder="e.g. UI/UX Designer, Backend Dev"
                        value={newOpportunity}
                        onChange={(e) => setNewOpportunity(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddOpportunity(); } }}
                      />
                      <Button type="button" variant="outline" onClick={handleAddOpportunity}>Add Role</Button>
                    </div>

                    {project.opportunities && project.opportunities.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {project.opportunities.map((role, index) => (
                          <div key={index} className="flex items-center gap-1 bg-green-100 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
                            {role}
                            <button
                              type="button"
                              onClick={() => handleRemoveOpportunity(role)}
                              className="text-green-600 hover:text-green-800"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Placeholder for Media Management */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Project Media</CardTitle>
              <CardDescription>(Coming Soon) Manage your project images and videos here.</CardDescription>
            </CardHeader>
            <CardContent className="text-center text-gray-500 py-10 border-2 border-dashed rounded-lg">
              <Upload className="h-8 w-8 mx-auto mb-2" />
              Media upload and management is under development.
            </CardContent>
          </Card>

          {/* Placeholder for Collaborators */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Collaborators</CardTitle>
              <CardDescription>(Coming Soon) Add other PortfolioHub users who worked on this project.</CardDescription>
            </CardHeader>
            <CardContent className="text-center text-gray-500 py-10 border-2 border-dashed rounded-lg">
              <Users className="h-8 w-8 mx-auto mb-2" />
              Collaborator management is under development.
            </CardContent>
          </Card>

          <div className="flex justify-end gap-4">
            <Link to="/dashboard/projects">
              <Button type="button" variant="outline" disabled={isSaving}>
                Cancel
              </Button>
            </Link>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProject;